import React, { useState, useEffect, useContext, useRef } from 'react';
import { fetchShortenedLinks } from './api';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../AuthProvider';
import '../css/Dashboard.css';
import 'react-datepicker/dist/react-datepicker.css';
import Chart from 'chart.js/auto';
import { io } from 'socket.io-client';
import PostForm from './PostForm';
import Footer from '../components/Footer';

const socket = io('https://urlshortener-serverside.onrender.com', {
  transports: ['websocket', 'polling'], // Ensures it works even if WebSocket is blocked
  withCredentials: true,
});

function NewDashboard() {
  const chartRef = useRef(null);
  const { isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const [links, setLinks] = useState([]);
  const [detailedView, setDetailedView] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dailyData, setDailyData] = useState([]);
  const [dailyUniqueData, setDailyUniqueData] = useState([]);
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const data = await fetchShortenedLinks();
        setLinks(data);
      } catch (error) {
        console.error('Failed to fetch links:', error);
      }
    };

    fetchLinks();
    socket.on('updateLinks', (data) => {
      setLinks(data);
    });

    return () => socket.off('updateLinks');
  }, []);

  useEffect(() => {
    if (selectedLink) {
      renderChart();
    }
  }, [selectedLink]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  const renderChart = () => {
    if (chartRef.current !== null) {
      chartRef.current.destroy();
    }

    const canvas = document.getElementById('linkClicksChart');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const shortUrls = links.map(link => link.shortUrl);
    const clickCounts = links.map(link => link.clicks);

    chartRef.current = new Chart(ctx, {
      type: 'pie',
      data: {
        labels: shortUrls,
        datasets: [
          {
            label: 'Total Link Clicks',
            data: clickCounts,
            backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0'],
            borderColor: '#fff',
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            position: 'top',
          },
        },
      },
    });
  };

  const fetchData = async (url, setter) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch data');
      const data = await response.json();
      setter(data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    if (detailedView && selectedLink) {
      fetchData(`https://urlshortener-serverside.onrender.com/api/charts/${selectedLink.shortUrl}/daily`, setDailyData);
      fetchData(`https://urlshortener-serverside.onrender.com/api/charts/${selectedLink.shortUrl}/dailyUniqueVisitors`, setDailyUniqueData);
    }
  }, [detailedView, selectedLink]);

  const copyToClipboard = (shortUrl) => {
    const fullUrl = `${window.location.origin}/${shortUrl}`;
    navigator.clipboard.writeText(fullUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      })
      .catch((error) => {
        console.error('Failed to copy full URL:', error);
      });
  };

  return (
    <div className={`dashboard-container ${theme}`}>
      <div className="header">
        <h1>Dashboard</h1>
        <button onClick={toggleTheme} className="theme-toggle">
          {theme === 'light' ? '🌙 Dark Mode' : '☀️ Light Mode'}
        </button>
        <div className="toggle-buttons">
          <button className={!detailedView ? 'active' : ''} onClick={() => setDetailedView(false)}>Basic View</button>
          <button className={detailedView ? 'active' : ''} onClick={() => setDetailedView(true)}>Detailed View</button>
        </div>
      </div>

      {detailedView ? (
        <div className="detailed-view">
          <PostForm />
          {selectedLink && (
            <div className="details">
              <div className="chart-container">
                <canvas id="linkClicksChart" width="800" height="400"></canvas>
              </div>
              <h6>Visitors by Country:</h6>
              <ul>
                {selectedLink.uniqueVisitors.map((visitor, index) => (
                  <li key={index}>
                    IP: {visitor.ip}, Country: {visitor.country || 'Unknown'}
                  </li>
                ))}
              </ul>
              <h6>Short URL: {window.location.origin}/{selectedLink.shortUrl}</h6>
              <h6>Long URL: {selectedLink.longUrl}</h6>
              <h6>Link Clicks: {selectedLink.clicks}</h6>
              <button onClick={() => copyToClipboard(selectedLink.shortUrl)}>Copy Short URL</button>
              {copied && <span className="copy-message">Link copied to clipboard!</span>}
            </div>
          )}
        </div>
      ) : (
        <div className="basic-view">
          <h6>Welcome to the Dashboard!</h6>
          <ul>
            {links.map((link) => (
              <li key={link._id}>
                <h6>Short URL: {window.location.origin}/{link.shortUrl}</h6>
                <h6>Long URL: {link.longUrl}</h6>
                <h6>Link Clicks: {link.clicks}</h6>
                <button onClick={() => setSelectedLink(link)}>View Details</button>
                <button onClick={() => copyToClipboard(link.shortUrl)} className={copied ? 'copied' : ''}>Copy Short URL</button>
                {copied && <span className="copy-message">Link copied to clipboard!</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      <Footer />
    </div>
  );
}

export default NewDashboard;
